/**
 * The body /create-comic sends to POST /api/books/create-manga-preview. Pure, so the payload is unit-tested.
 */
import { getLanguageOptions, isLanguageSupported, isComicLanguage } from '@/utils/languages';
import type { MangaSettings } from '@/types/manga';
import type { MangaCharacterDraft, MangaPhoto } from './types';
import { photoConsentJson } from '@/components/consent/photo-consent-gate';
import { guardCast, typedRelativesCovered, typedRelativesNotInPhoto, type TypedRelativesChoice } from '@/utils/manga/family-guard';

/** The picture-book wizard's language list narrowed to the Latin-alphabet languages comics are offered in. */
export function comicLanguageOptions() {
  return getLanguageOptions().filter((o) => isComicLanguage(o.value));
}

/** The language code the request sends: a supported code, else English. */
export function payloadLanguage(language: string | null | undefined): string {
  return language && isLanguageSupported(language) ? language : 'en';
}

/** The chosen catalogue story as the flow holds it: the seed's id and length, and its idea as filled for this family. */
export interface ComicStoryChoice {
  id: string;
  idea: string;
  pageCount: number;
}

/**
 * The storyId to send (stories-as-comics): only while the parent is still on the story, i.e. the idea is the seeded
 * one and the length is the seed's. An edited idea makes the comic free-form; an edited title keeps the story.
 * English only for now (batch A L4): a stamped comic in another language has not been tested yet, so it is
 * free-form. `language` is the payload's (an unsupported code is English).
 */
export function comicStoryIdFor(story: ComicStoryChoice | null | undefined, storyIdea: string, pageCount: number, language: string): string | undefined {
  if (!story) return undefined;
  if (String(language || 'en').trim().toLowerCase().split(/[-_]/)[0] !== 'en') return undefined;
  return storyIdea.trim() === story.idea.trim() && pageCount === story.pageCount ? story.id : undefined;
}

export function buildComicPreviewPayload(i: {
  characters: MangaCharacterDraft[];
  storyTitle: string;
  storyIdea: string;
  settings: MangaSettings;
  language: string;
  photo: MangaPhoto;
  /** The parent's typed-relative choice: sent only when it covers the idea's current words (Task C3). */
  typedRelatives?: TypedRelativesChoice | null;
  /** The catalogue story chosen with ?story= (sent as storyId only by comicStoryIdFor's rule). */
  story?: ComicStoryChoice | null;
}) {
  const language = payloadLanguage(i.language);
  const storyId = comicStoryIdFor(i.story, i.storyIdea, i.settings.pageCount, language);
  // The seeded story idea is our text, the baseline (as on the style step): only a relative the parent typed counts
  const words = typedRelativesNotInPhoto(i.storyIdea, guardCast(i.characters), i.story ? { baseline: i.story.idea } : {});
  const sendChoice = words.length > 0 && typedRelativesCovered(words, i.typedRelatives);
  return {
    characters: i.characters.map((c) => ({
      name: c.name.trim(),
      age: Number(c.age) || 8,
      gender: c.gender,
      relationship: c.isMainCharacter ? undefined : c.relationship.trim() || undefined,
      isMainCharacter: c.isMainCharacter,
      faceCoordinates: c.faceCoordinates || undefined,
      // A head covering from the photo analysis (the server re-checks it against its allow-list)
      ...(c.headCovering ? { headCovering: c.headCovering } : {}),
    })),
    storyTitle: i.storyTitle.trim(),
    storyIdea: i.storyIdea.trim() || undefined,
    settings: i.settings,
    language,
    photoBase64: i.photo.dataUrl,
    photoDimensions: i.photo.dimensions,
    ...photoConsentJson(i.photo.photoConsentVersion),
    ...(sendChoice ? { typedRelatives: { words, choice: 'continue' as const } } : {}),
    ...(storyId ? { storyId } : {}),
  };
}
